import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { createQuery } from '../../api/Query';
import { CreateQueryRequest } from '../../types/Query';
import RoomsTravellersDropdown from '../Common/RoomsTravellersDropdown';
import { buildDefaultQuotationRooms, normalizeQuotationRooms, QuotationRoomOccupancy } from '../QuotationBuilder/quotationRoomOccupancy';
import '../../css/modal.css';

interface CreateQueryModalProps {
  customerId: number;
  onClose: () => void;
  onQueryCreated: (queryId: number) => void;
}

const CreateQueryModal: React.FC<CreateQueryModalProps> = ({ customerId, onClose, onQueryCreated }) => {
  const [paxRooms, setPaxRooms] = useState<QuotationRoomOccupancy[]>(() => buildDefaultQuotationRooms());
  const [formData, setFormData] = useState({
    type: '',
    location: '',
    description: '',
    queryDate: '',
    source: 'website',
  });

  const pax = useMemo(
    () => paxRooms.reduce((sum, room) => sum + Number(room.adults || 0) + Number(room.children || 0), 0),
    [paxRooms]
  );

  const roomsPayload = useMemo(
    () => normalizeQuotationRooms(paxRooms).map((room) => ({
      Adults: Math.max(1, Number(room.adults || 0)),
      Children: Math.max(0, Number(room.children || 0)),
      ChildrenAges: Array.isArray(room.childrenAges)
        ? room.childrenAges.map((age) => Number(age ?? 0))
        : [],
    })),
    [paxRooms]
  );

  const handlePaxChange = (rooms: QuotationRoomOccupancy[]) => {
    setPaxRooms(rooms);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.type) {
      toast.warning('Please select a query type.');
      return false;
    }
    if (!formData.location.trim()) {
      toast.warning('Please enter a location.');
      return false;
    }
    if (!formData.description.trim()) {
      toast.warning('Please enter a description.');
      return false;
    }
    if (!formData.source) {
      toast.warning('Please select a source.');
      return false;
    }
    if (!pax || pax <= 0) {
      toast.warning('Please enter a valid PAX number.');
      return false;
    }
    if (!formData.queryDate) {
      toast.warning('Please select a travel date.');
      return false;
    }
    const hasIncompleteChildAges = normalizeQuotationRooms(paxRooms).some((room) => (
      room.children > 0 && room.childrenAges?.some((age) => age === null || age === undefined)
    ));
    if (hasIncompleteChildAges) {
      toast.warning('Please select an age for each child.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const query: CreateQueryRequest = {
      customer_id: customerId,
      type: formData.type,
      location: formData.location,
      query_date: formData.queryDate,
      rooms: roomsPayload,
      description: formData.description,
      source: formData.source,
      status: 'New',
      engagement_level: '1',
    };

    try {
      const newQuery = await createQuery(query);
      toast.success('Query created successfully!');
      onQueryCreated(newQuery.id);
    } catch (error) {
      toast.error('Failed to create query.');
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal create-query-modal">
        <div className="modal-header">
          <h2>Add Query</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close modal" title="Close modal">
            ×
          </button>
        </div>

        <div className="create-query-modal__grid">
          <div className="create-query-modal__field">
            <label>
              Type<span className="required">*</span>
            </label>
            <select name="type" value={formData.type} onChange={handleChange} required>
              <option value="">Select type</option>
              <option value="domestic">Domestic</option>
              <option value="international">International</option>
              <option value="flight">Flight</option>
              <option value="hotel">Hotel</option>
              <option value="event">Event</option>
              <option value="visa">VISA</option>
            </select>
          </div>

          <div className="create-query-modal__field">
            <label>
              Location<span className="required">*</span>
            </label>
            <input name="location" placeholder="e.g. Goa, Paris" value={formData.location} onChange={handleChange} />
          </div>

          <div className="create-query-modal__field">
            <label>
              Source<span className="required">*</span>
            </label>
            <select name="source" value={formData.source} onChange={handleChange} required>
              <option value="website">Website</option>
              <option value="referral">Referral</option>
              <option value="market-visit">Market Visit</option>
              <option value="online">Online</option>
              <option value="others">Others</option>
            </select>
          </div>

          <div className="create-query-modal__field">
            <label>PAX<span className="required">*</span></label>
            <RoomsTravellersDropdown value={paxRooms} onChange={handlePaxChange} />
          </div>

          <div className="create-query-modal__field">
            <label>
              Travel Date<span className="required">*</span>
            </label>
            <input type="date" name="queryDate" value={formData.queryDate} onChange={handleChange} />
          </div>

          <div className="create-query-modal__field create-query-modal__field--full">
            <label>
              Description<span className="required">*</span>
            </label>
            <textarea
              name="description"
              placeholder="Additional details about the query..."
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={handleSubmit} className="primary">Save</button>
        </div>
      </div>
    </div>
  );
};

export default CreateQueryModal;
